import { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { shallowEqual, useSelector } from 'react-redux';

import { FEATURE_FLAGS } from 'app/constants/FeatureFlag';
import { ROUTES_PATH } from 'app/constants/Routes';
import {
  readAcademyMode,
  writeAcademyMode,
  type AcademyMode,
} from 'app/helpers/academyMode';
import { isAcademyForcedUser, isAcademyPlan } from 'app/helpers/academyPlan';
import useUnleashFeatureFlagAdapter from 'app/adapter/UnleashFeatureFlagAdapter';

type SubscriptionSlice = {
  auth: { user?: { id?: number | string; is_student?: boolean } };
  subscription: {
    subscription_status?: {
      plan_details?: {
        id?: number | string | null;
        gateway_id?: number | string | null;
      };
    };
    is_subscription_status_loading?: boolean;
  };
};

const HINT_KEY = 'jusfy:isAcademyUser';

/** Chave da query das flags do aluno (acesso + troca), por usuário. */
export const academyFlagsQueryKey = (userId?: number | string | null) =>
  ['academy-flags', userId ?? null] as const;

/**
 * O slice de assinatura não é persistido, então `is_subscription_status_loading`
 * é `true` em todo carregamento de página. Sem um palpite, o sidebar desenharia
 * o menu do advogado (Processos, Clientes, Jusfy Pay…) antes de descobrir que o
 * usuário é aluno — e o menu inteiro trocaria debaixo do cursor.
 *
 * O hint guarda a última resposta conhecida por usuário e vale só enquanto a
 * assinatura carrega; assim que ela chega, o valor real manda e o hint é
 * reescrito. Não é fonte de verdade: adiantar cinco itens de menu é o pior que
 * ele pode fazer errado, e as rotas continuam guardadas pelo valor real.
 */
const readHint = (userId?: number | string): boolean => {
  if (userId === undefined || userId === null) return false;
  try {
    return window.localStorage.getItem(HINT_KEY) === String(userId);
  } catch {
    return false;
  }
};

const writeHint = (userId: number | string | undefined, isAcademy: boolean) => {
  if (userId === undefined || userId === null) return;
  try {
    if (isAcademy) window.localStorage.setItem(HINT_KEY, String(userId));
    else if (window.localStorage.getItem(HINT_KEY) === String(userId)) {
      window.localStorage.removeItem(HINT_KEY);
    }
  } catch {
    /* modo privado / storage cheio: seguimos sem hint. */
  }
};

/**
 * O usuário logado é do plano academy/JusRecruta (aluno)?
 *
 * Duas condições, as duas obrigatórias:
 * 1. o `/me` diz `is_student` (coluna `clients.is_student`, marcada no cadastro
 *    universitário ou por admin) — ou, como fallback, o plano da assinatura
 *    está em `REACT_APP_ACADEMY_PLAN_IDS`;
 * 2. a flag `RELEASE.ACADEMY` (Unleash, release por user_id) está ligada para
 *    ele — é a rede de segurança: o menu e a dash do aluno só trocam para quem
 *    a gente liberou, mesmo que um plano entre na lista por engano.
 *
 * Ter acesso não é a mesma coisa que estar vendo a Academy. O aluno que também
 * é advogado ganha a flag `RELEASE.ACADEMY_SWITCH` (liberada na mão, por
 * user_id) e vira **dual**: usa os dois apps, um de cada vez, e alterna pelo
 * botão do sidebar (`setAcademyMode`). O modo escolhido fica no localStorage
 * (helpers/academyMode). Para ele, `isAcademyUser` só é verdadeiro no modo
 * `academy`; no modo `advogado` o app se comporta exatamente como para um
 * advogado. Quem não tem a flag não tem modo: é sempre `academy`.
 *
 * As flags são avaliadas por usuário (endpoint has-permission-by-user-id), não
 * a flag global sem contexto do `useFeatureFlag` — release por user_id só
 * existe por esse caminho. As duas vão numa query só do React Query,
 * compartilhada por todo mundo que monta este hook (BasePage, Sidebar,
 * Dashboard, botão de troca…): uma rodada de requests por page load, e o mesmo
 * valor em todos. Ver `academyFlagsQueryKey`.
 *
 * `isLoading` fica exposto para quem precisa esperar o valor confirmado antes de
 * uma ação irreversível (redirect, por exemplo) em vez de agir pelo hint.
 */
export function useIsAcademyUser(): {
  isAcademyUser: boolean;
  isLoading: boolean;
  isDualUser: boolean;
  academyMode: AcademyMode;
  setAcademyMode: (mode: AcademyMode) => void;
} {
  const { userId, isStudent, planDetails, isSubscriptionLoading } = useSelector(
    (state: SubscriptionSlice) => ({
      userId: state.auth?.user?.id,
      isStudent: state.auth?.user?.is_student === true,
      planDetails: state.subscription?.subscription_status?.plan_details,
      isSubscriptionLoading: Boolean(
        state.subscription?.is_subscription_status_loading,
      ),
    }),
    shallowEqual,
  );

  const hasAcademyPlan = isAcademyPlan(planDetails);
  /* `is_student` vem no `/me`, que carrega antes da assinatura: o aluno do
     cadastro universitário nem depende de plan_details. */
  const hasAcademyAccess = isStudent || hasAcademyPlan;

  /* As flags só decidem algo para quem tem acesso. Para os demais o hook nem
     pergunta ao Unleash (query desligada = resolvida e desligada): este hook
     monta em vários lugares por página, e sem isso seriam requests de
     has-permission-by-user-id para TODO usuário. `staleTime: Infinity` porque
     a flag é avaliada por page load, como o hint. O adapter já devolve false
     em erro de rede, então a query não falha por ele. */
  const { isUserEnabled } = useUnleashFeatureFlagAdapter();
  const canAskFlags =
    hasAcademyAccess && userId !== undefined && userId !== null;
  const flagsQuery = useQuery({
    queryKey: academyFlagsQueryKey(userId),
    queryFn: async () => {
      const [access, switchEnabled] = await Promise.all([
        isUserEnabled(FEATURE_FLAGS.RELEASE.ACADEMY),
        isUserEnabled(FEATURE_FLAGS.RELEASE.ACADEMY_SWITCH),
      ]);
      return { access, switchEnabled };
    },
    enabled: canAskFlags,
    staleTime: Infinity,
    retry: false,
  });
  const isFlagEnabled = flagsQuery.data?.access === true;
  const isSwitchEnabled = flagsQuery.data?.switchEnabled === true;
  const flagsResolved = flagsQuery.isSuccess || flagsQuery.isError;

  /*
   * O atalho de desenvolvimento não depende nem da assinatura nem da flag:
   * vale pelo user_id. Quando ele pega, a resposta já sai pronta
   * (`isLoading: false`) — esperar só atrasaria a tela sem mudar o resultado.
   */
  const forced = isAcademyForcedUser(userId);
  const hasAcademy = forced || (hasAcademyAccess && isFlagEnabled);

  /* Dual = aluno com a flag de troca. Quem não é dual não tem modo: é sempre
     `academy`, e um modo esquecido no storage é ignorado. O atalho de dev
     continua abrindo só a Academy. */
  const isDualUser = !forced && hasAcademy && isSwitchEnabled;
  const academyMode: AcademyMode = isDualUser
    ? readAcademyMode(userId)
    : 'academy';
  const resolved = hasAcademy && academyMode === 'academy';

  /* Só a PRIMEIRA carga da assinatura conta como "carregando". Recargas
     (refresh de regularização, sucesso de pagamento, FORCE_LOAD) religam
     is_subscription_status_loading com plan_details ainda no store; tratar isso
     como loading desmontava a Dashboard inteira a cada refresh. */
  const isFirstSubscriptionLoad =
    isSubscriptionLoading && planDetails === undefined && !isStudent;
  const isLoading =
    !forced && (isFirstSubscriptionLoad || (canAskFlags && !flagsResolved));

  useEffect(() => {
    if (!isLoading) writeHint(userId, resolved);
  }, [isLoading, resolved, userId]);

  /* Trocar de modo recarrega o app na home em vez de sincronizar estado na
     mão: menu, dash, rotas e o hint nascem já no modo certo. */
  const setAcademyMode = useCallback(
    (mode: AcademyMode) => {
      writeAcademyMode(userId, mode);
      writeHint(userId, mode === 'academy');
      window.location.assign(ROUTES_PATH.MAIN.DASHBOARD);
    },
    [userId],
  );

  return {
    isAcademyUser: isLoading ? readHint(userId) : resolved,
    isLoading,
    isDualUser,
    academyMode,
    setAcademyMode,
  };
}

export default useIsAcademyUser;
